import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../api';
import { useAuthStore } from '../../store';
import './Login.css';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 로그인 폼
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  // 회원가입 폼
  const [regForm, setRegForm] = useState({
    username: '', password: '', password_confirm: '',
    email: '', name: '', phone: '',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.login(loginForm.username, loginForm.password);
      setAuth(res.data.user, res.data.token);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regForm.username || !regForm.password) {
      setError('아이디와 비밀번호는 필수입니다.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authAPI.register(regForm);
      alert('회원가입이 완료되었습니다.\n관리자 승인 후 로그인이 가능합니다.');
      setTab('login');
      setRegForm({ username: '', password: '', password_confirm: '', email: '', name: '', phone: '' });
    } catch (err: any) {
      const data = err.response?.data;
      if (typeof data === 'object') {
        const msg = Object.values(data).flat().join('\n');
        setError(msg);
      } else {
        setError('회원가입에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg-circle c1" />
        <div className="login-bg-circle c2" />
        <div className="login-bg-circle c3" />
      </div>

      <div className="login-container">
        {/* 로고 */}
        <div className="login-logo">
          <div className="login-logo-icon">M</div>
          <div>
            <h1 className="login-logo-title">Mohabwe</h1>
            <p className="login-logo-sub">전기기사 CBT 학습 플랫폼</p>
          </div>
        </div>

        {/* 탭 */}
        <div className="login-tabs">
          <button
            className={`login-tab ${tab === 'login' ? 'active' : ''}`}
            onClick={() => { setTab('login'); setError(''); }}
          >로그인</button>
          <button
            className={`login-tab ${tab === 'register' ? 'active' : ''}`}
            onClick={() => { setTab('register'); setError(''); }}
          >회원가입</button>
          <div className={`login-tab-indicator ${tab === 'register' ? 'right' : ''}`} />
        </div>

        {/* 에러 */}
        {error && (
          <div className="login-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* 로그인 폼 */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label className="form-label">아이디</label>
              <input
                className="form-control"
                placeholder="아이디를 입력하세요"
                value={loginForm.username}
                onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input
                type="password"
                className="form-control"
                placeholder="비밀번호를 입력하세요"
                value={loginForm.password}
                onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>
        )}

        {/* 회원가입 폼 */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="login-form">
            <div className="form-group">
              <label className="form-label">아이디 <span className="required">*</span></label>
              <input
                className="form-control"
                placeholder="아이디 (4자 이상)"
                value={regForm.username}
                onChange={e => setRegForm({ ...regForm, username: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호 <span className="required">*</span></label>
              <input
                type="password"
                className="form-control"
                placeholder="비밀번호 (4자 이상)"
                value={regForm.password}
                onChange={e => setRegForm({ ...regForm, password: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호 확인 <span className="required">*</span></label>
              <input
                type="password"
                className="form-control"
                placeholder="비밀번호 재입력"
                value={regForm.password_confirm}
                onChange={e => setRegForm({ ...regForm, password_confirm: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">이메일</label>
              <input
                type="email"
                className="form-control"
                placeholder="이메일 (선택)"
                value={regForm.email}
                onChange={e => setRegForm({ ...regForm, email: e.target.value })}
              />
            </div>
            <div className="reg-row">
              <div className="form-group">
                <label className="form-label">이름</label>
                <input
                  className="form-control"
                  placeholder="이름 (선택)"
                  value={regForm.name}
                  onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">연락처</label>
                <input
                  className="form-control"
                  placeholder="010-0000-0000"
                  value={regForm.phone}
                  onChange={e => setRegForm({ ...regForm, phone: e.target.value })}
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={loading}
            >
              {loading ? '처리 중...' : '회원가입'}
            </button>
            <p className="login-notice">
              ※ 가입 후 관리자 승인이 필요합니다.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
